/**
 * ArticleCharts
 * Mountet ECharts-Diagramme in Artikel-Figuren der Form
 *
 *   <figure class="article-chart" data-echart="ausbreitung">
 *     <figcaption>…</figcaption>
 *   </figure>
 *
 * Der Datensatz liegt unter <base>charts/<id>.json und wird erst geladen,
 * wenn die Figur in die Naehe des Sichtfelds kommt. ECharts selbst kommt per
 * dynamischem Import, damit Artikel ohne Diagramm die Bibliothek nicht
 * mitschleppen.
 *
 * Faellt etwas aus (kein Netz, unbekannter Typ, ECharts laedt nicht), bleibt
 * die Bildunterschrift stehen und der Artikel ist weiter lesbar.
 */

const SELECTOR = 'figure.article-chart[data-echart]';

export default class ArticleCharts {
	constructor(root = '[data-article-body]') {
		this.body = document.querySelector(root);
		if (!this.body) return;

		this.figures = Array.from(this.body.querySelectorAll(SELECTOR));
		if (!this.figures.length) return;

		this.charts = [];
		this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		this.figures.forEach((fig) => this.prepare(fig));
		this.observe();
		this.bindResize();
	}

	/** Zeichenflaeche und Ladezustand anlegen, bevor irgendetwas laedt. */
	prepare(fig) {
		const canvas = document.createElement('div');
		canvas.className = 'article-chart__canvas';
		canvas.setAttribute('role', 'img');

		const caption = fig.querySelector('figcaption');
		if (caption) {
			canvas.setAttribute('aria-label', caption.textContent.trim());
			fig.insertBefore(canvas, caption);
		} else {
			fig.appendChild(canvas);
		}

		fig.dataset.state = 'idle';
		fig._canvas = canvas;
	}

	observe() {
		const io = new IntersectionObserver((entries) => {
			entries.forEach((e) => {
				if (!e.isIntersecting) return;
				io.unobserve(e.target);
				this.mount(e.target);
			});
		}, { rootMargin: '300px 0px' });

		this.figures.forEach((fig) => io.observe(fig));
	}

	async mount(fig) {
		if (fig.dataset.state !== 'idle') return;
		fig.dataset.state = 'loading';

		const id = fig.dataset.echart;
		const base = (import.meta.env && import.meta.env.BASE_URL) || '/';

		try {
			const [echarts, buildOptionMod, themeMod, data] = await Promise.all([
				import('../charts/echartsCore.js'),
				import('../charts/buildOption.js'),
				import('../charts/philflowTheme.js'),
				fetch(`${base}charts/${id}.json`).then((r) => {
					if (!r.ok) throw new Error(`${r.status} für ${id}`);
					return r.json();
				}),
			]);

			const { NARROW } = buildOptionMod;
			const narrow = () => fig._canvas.clientWidth < NARROW;

			const option = buildOptionMod.default(data, { narrow: narrow() });
			if (!option) throw new Error(`unbekannter Diagrammtyp "${data.type}"`);

			echarts.registerTheme(themeMod.themeName, themeMod.theme);

			// Kein useDirtyRect: Diese Diagramme aendern sich nach dem Zeichnen
			// nicht mehr, die Optimierung bringt also nichts — und sie hat beim
			// Einhaengen waehrend des Scrollens nachweislich Teile der Flaeche
			// ungezeichnet gelassen (21 Balken angelegt, 3 gezeichnet).
			const chart = echarts.init(fig._canvas, themeMod.themeName, { renderer: 'canvas' });
			chart.setOption({ ...option, animation: !this.reduced });

			// Die Flaeche kann sich ohne Fenster-Resize aendern (Marginalspalte,
			// Schriften, Layoutwechsel). Ein Beobachter auf der Flaeche selbst
			// trifft das, ein Fenster-Listener allein nicht.
			//
			// Wechselt dabei die Breitenklasse, reicht resize() nicht: manche
			// Diagramme stellen schmal auf eine andere Beschriftung um.
			let wasNarrow = narrow();
			const ro = new ResizeObserver(() => {
				const isNarrow = narrow();
				if (isNarrow !== wasNarrow) {
					wasNarrow = isNarrow;
					chart.setOption(
						{ ...buildOptionMod.default(data, { narrow: isNarrow }), animation: false },
						{ notMerge: true },
					);
				}
				chart.resize();
			});
			ro.observe(fig._canvas);

			this.charts.push(chart);
			fig.dataset.state = 'ready';
		} catch (err) {
			fig.dataset.state = 'failed';
			fig._canvas.remove();
			// Bewusst nur eine Warnung: ein fehlendes Diagramm darf den
			// Artikel nicht beschaedigen.
			console.warn('[ArticleCharts]', err.message);
		}
	}

	bindResize() {
		let t = null;
		window.addEventListener('resize', () => {
			clearTimeout(t);
			t = setTimeout(() => this.charts.forEach((c) => c.resize()), 150);
		}, { passive: true });
	}
}
